import { computeFormItemName } from '@components/localizable/utils'
import { useLocalizable } from '@contexts/LocalizableContext'
import { AvailableLocale } from '@models/Lang'
import { Form } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import { useCallback, useEffect, useState } from 'react'
import { langList } from '../../src/factories/Utils'

export type LocalizableInputProps = {
  selectedLangProps?: AvailableLocale
  name: NamePath
  namePrefix?: string[]
  hideLangSelector?: boolean
}

export const useLocalizableInput = ({
  selectedLangProps,
  name,
  namePrefix,
  hideLangSelector = false,
}: LocalizableInputProps) => {
  const localizable = useLocalizable()
  const formInstance = Form.useFormInstance()

  const [selectedLangCode, setSelectedLangCode] = useState<AvailableLocale>(
    selectedLangProps ?? localizable?.langCode ?? langList[0].code
  )

  // A field pinned through `selectedLangProps` edits that language and only that
  // one, so it wins over the provider: forms that lay out one field per language
  // (e.g. the WhatsApp bot settings) must not collapse onto the provider's locale.
  useEffect(() => {
    if (selectedLangProps !== undefined) {
      setSelectedLangCode(selectedLangProps)
    } else if (localizable?.langCode) {
      setSelectedLangCode(localizable.langCode)
    }
  }, [localizable?.langCode, selectedLangProps])

  // Announce this field to the provider so a header selector can auto-detect
  // whether the open form holds localizable fields. registerField is stable.
  // Pinned fields stay out of the count: the header selector does not drive them,
  // so they must not make it appear.
  useEffect(() => {
    if (selectedLangProps !== undefined) {
      return
    }
    return localizable?.registerField()
  }, [localizable?.registerField, selectedLangProps])

  const formItemName = useCallback(
    (lang: AvailableLocale): NamePath =>
      computeFormItemName(namePrefix, name, lang),
    [namePrefix, name]
  )

  const value = Form.useWatch(formItemName(selectedLangCode), formInstance)

  // When the enclosing surface owns the selector (SBRM drawer header, push
  // wizard header), fields never render their own. `hideLangSelector` stays as
  // the manual override for a field that must opt out on its own.
  const showLangSelector = !hideLangSelector && !localizable?.headerSelector

  // The header drives the language, so the field drops its own selector but
  // still shows a passive flag stating which locale it edits. A manual
  // `hideLangSelector` opt-out shows nothing at all.
  const showLangFlag = !!localizable?.headerSelector && !hideLangSelector

  return {
    selectedLangCode,
    setSelectedLangCode,
    formItemName,
    formInstance,
    value,
    showLangSelector,
    showLangFlag,
  }
}
